import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import { usePublishProgress } from "@/components/PublishProgress";
import { AgentLabelBadge, agentLabelTitle, type AgentLabelFields } from "@/components/AgentLabelBadge";
import { LabelReportDialog } from "@/components/LabelReportDialog";

/* Fresh Deck — fullscreen story-mode review of newly-posted owner-direct
 * listings. The photo IS the card: info rides on gradient scrims, photos
 * switch IG-style (tap zones + segment bar), the full description opens in a
 * glass bottom sheet. Swipe left (reject: never shown to YOU again) or right
 * (publish: spawns the real background publish job; the floating
 * PublishProgress panel tracks it). Pool = last 24h, widening by 24h as it
 * drains, capped at 7 days.
 *
 * Rendered through a portal as a fixed z-[1400] overlay (above chat z-1100 and
 * notification modals z-1300). The overlay is ALWAYS
 * dark regardless of app theme, so it deliberately avoids `.card`/`.badge`/
 * `text-slate-*` (all remapped under html.theme-light) in favour of
 * theme-independent white/…-alpha + literal hex colours. */

interface DeckPhone {
  number: string;
  is_main: boolean;
  has_viber: boolean;
  has_whatsapp: boolean;
}

interface DeckListing {
  application_id: number;
  source: "ss" | "mh";
  url: string;
  deal_type: string | null;
  real_estate_type: string | null;
  city: string | null;
  district: string | null;
  subdistrict: string | null;
  street: string | null;
  address: string | null;
  price_usd: number | null;
  price_geo: number | null;
  unit_price_usd: number | null;
  area: number | null;
  rooms: number | null;
  floor: number | null;
  floors_total: number | null;
  contact_person: string | null;
  seller_type: string | null;
  // The deck is server-filtered to owners with undeclared agencies excluded,
  // so this should always be null. Rendered anyway rather than trusted — a
  // detector gap should be visible on the card, not silently mislabelled.
  undeclared_agency: "company" | "volume" | null;
  agency_name: string | null;
  phones: DeckPhone[];
  image: string | null;
  images: string[];
  title: string | null;
  description: string | null;
  create_date: string | null;
  // Agent-posture verdict from the text (app/services/fresh_labels).
  agent_label: AgentLabelFields["agent_label"];
  agent_label_confidence: number | null;
  agent_label_reason: string | null;
  agent_label_evidence: string | null;
  agent_label_source: AgentLabelFields["agent_label_source"];
}

interface SwipeFeedResponse {
  items: DeckListing[];
  window_days: number;
  extended: boolean;
  exhausted: boolean;
  pool_count: number;
}

const SOURCE_LABEL: Record<string, string> = { ss: "SS.ge", mh: "MyHome" };
const SWIPE_THRESHOLD = 120;
const TAP_MAX_DIST = 10;
const TAP_MAX_MS = 300;

function keyOf(l: DeckListing): string {
  return `${l.source}-${l.application_id}`;
}

function fmtPrice(v: number | null): string {
  if (v == null) return "—";
  return "$" + Math.round(v).toLocaleString();
}

function timeAgo(s: string | null): string {
  if (!s) return "—";
  const ms = Date.now() - new Date(s).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString(undefined, {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function isHot(s: string | null): boolean {
  if (!s) return false;
  return Date.now() - new Date(s).getTime() < 3 * 3600_000;
}

/* MyHome descriptions arrive with raw HTML (<br />, tags, entities) — flatten
 * to plain text with real newlines before rendering. */
function cleanDescription(s: string | null): string | null {
  if (!s) return null;
  const txt = s
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/?p[^>]*>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return txt || null;
}

function locationLine(l: DeckListing): string {
  const parts = [l.district, l.subdistrict, l.street || l.address].filter(Boolean);
  return parts.join(" · ") || l.city || "—";
}

function photosOf(l: DeckListing): string[] {
  return l.images.length > 0 ? l.images : l.image ? [l.image] : [];
}

/* Single-path lucide-style icon. */
function Ic({ d, size = 14, sw = 1.9, className }: { d: string; size?: number; sw?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}

const IC = {
  flame:
    "M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z",
  clock: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2",
  x: "M18 6 6 18 M6 6l12 12",
  send: "m22 2-7 20-4-9-9-4 20-7z M22 2 11 13",
  area: "M15 3h6v6 M9 21H3v-6 M21 3l-7 7 M3 21l7-7",
  rooms: "M2 4v16 M2 8h18a2 2 0 0 1 2 2v10 M2 17h20 M6 8v9",
  floor: "M3 21h18 M5 21V7l7-4 7 4v14 M9 9h1 M14 9h1 M9 13h1 M14 13h1 M9 17h1 M14 17h1",
  pin: "M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  phone:
    "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z",
  open: "M15 3h6v6 M10 14 21 3 M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6",
  chevL: "m15 18-6-6 6-6",
  chevR: "m9 18 6-6-6-6",
  chevD: "m6 9 6 6 6-6",
  tag: "M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z M7.5 8.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
  user: "M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
};

export function FreshDeck({ onExit }: { onExit: () => void }) {
  const { register } = usePublishProgress();
  const [queue, setQueue] = useState<DeckListing[]>([]);
  // "Wrong label?" dialog target — rendered inline inside the story frame.
  const [reportFor, setReportFor] = useState<DeckListing | null>(null);
  const [meta, setMeta] = useState<{ window_days: number; extended: boolean; exhausted: boolean; pool_count: number }>({
    window_days: 1, extended: false, exhausted: false, pool_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [blockErr, setBlockErr] = useState<"creds" | "scraper" | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [stats, setStats] = useState({ reviewed: 0, published: 0 });
  const [busySwipe, setBusySwipe] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const seen = useRef<Set<string>>(new Set());

  const fetchMore = useCallback(async () => {
    setFetching(true);
    try {
      const r = await api<SwipeFeedResponse>("/api/fresh/swipe-feed?limit=10");
      setMeta({
        window_days: r.window_days, extended: r.extended,
        exhausted: r.exhausted, pool_count: r.pool_count,
      });
      setQueue((q) => {
        const have = new Set(q.map(keyOf));
        const fresh = r.items.filter(
          (i) => !have.has(keyOf(i)) && !seen.current.has(keyOf(i)),
        );
        return [...q, ...fresh];
      });
      setBlockErr(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 503) setBlockErr("scraper");
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchMore(); }, [fetchMore]);
  useEffect(() => {
    if (!fetching && !meta.exhausted && queue.length > 0 && queue.length <= 3) {
      void fetchMore();
    }
  }, [queue.length, fetching, meta.exhausted, fetchMore]);

  // Fullscreen overlay: lock body scroll; ESC closes the sheet first, then
  // exits the deck (the app-wide pattern from ConfirmDialog).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (sheetOpen) setSheetOpen(false);
      else onExit();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheetOpen, onExit]);

  const advance = useCallback((l: DeckListing) => {
    seen.current.add(keyOf(l));
    setSheetOpen(false);
    setQueue((q) => q.filter((x) => keyOf(x) !== keyOf(l)));
    setMeta((m) => ({ ...m, pool_count: Math.max(0, m.pool_count - 1) }));
  }, []);

  const doReject = useCallback(
    (l: DeckListing) => {
      advance(l);
      setStats((s) => ({ ...s, reviewed: s.reviewed + 1 }));
      void api("/api/fresh/swipe", {
        method: "POST",
        json: { source: l.source, external_id: String(l.application_id), action: "reject" },
      }).catch(() => undefined);
    },
    [advance],
  );

  const doPublish = useCallback(
    async (l: DeckListing) => {
      if (busySwipe) return;
      setBusySwipe(true);
      setNote(null);
      try {
        const r = await api<{ job_id: string | null; job_run_id: string | null }>(
          "/api/fresh/swipe",
          {
            method: "POST",
            json: {
              source: l.source,
              external_id: String(l.application_id),
              action: "publish",
              // Always cross-post: SS.ge silently downgrades to MyHome-only
              // when the agent has no SS credentials (bulk-publish semantics).
              use_ss: true,
            },
          },
        );
        if (r.job_id && r.job_run_id) {
          register({
            jobId: r.job_id,
            jobRunId: r.job_run_id,
            origin: "single",
            title: l.title,
            sourceUrl: l.url,
            agentLabel: null,
            agentAvatarUrl: null,
            useSs: true,
          });
        }
        advance(l);
        setStats((s) => ({ reviewed: s.reviewed + 1, published: s.published + 1 }));
      } catch (e) {
        if (e instanceof ApiError && e.message === "missing_myhome_credentials") {
          setBlockErr("creds");
        } else if (e instanceof ApiError && e.message === "listing_unavailable") {
          // Gone from the portal — auto-reject so it stops appearing.
          setNote("That listing is gone from the portal — skipped.");
          doReject(l);
        } else {
          setNote(e instanceof Error ? e.message : "Publish failed — try again.");
        }
      } finally {
        setBusySwipe(false);
      }
    },
    [advance, busySwipe, doReject, register],
  );

  const top = queue[0] ?? null;
  const next = queue[1] ?? null;

  // Keyboard: ← reject · → publish (photos are ↑/↓ inside StoryCard).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!top || blockErr || busySwipe || sheetOpen) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); doReject(top); }
      if (e.key === "ArrowRight") { e.preventDefault(); void doPublish(top); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [top, blockErr, busySwipe, sheetOpen, doReject, doPublish]);

  // Prefetch the next card's cover so the reveal under a fly-out is instant.
  useEffect(() => {
    const cover = next ? photosOf(next)[0] : null;
    if (cover) { const im = new Image(); im.src = cover; }
  }, [next]);

  const windowLabel = meta.window_days === 1 ? "24h" : `${meta.window_days}d`;
  const poolLabel = meta.exhausted && queue.length === 0
    ? "0"
    : `${meta.pool_count >= 200 ? "200+" : meta.pool_count}`;

  return createPortal(
    <div className="fixed inset-0 z-[1400] bg-black sm:bg-black/85 sm:backdrop-blur-md">
      <div className="h-full w-full flex items-center justify-center">
        {/* Story frame: edge-to-edge on mobile, centered 9:16 on desktop */}
        <div className="relative h-full w-full overflow-hidden bg-black sm:h-[min(92vh,860px)] sm:w-auto sm:aspect-[9/16] sm:rounded-3xl sm:border sm:border-white/10 sm:shadow-2xl sm:shadow-black/60">
          {note && (
            <div className="absolute top-16 inset-x-4 z-50 text-[11.5px] text-amber-200 bg-amber-950/80 border border-amber-500/30 backdrop-blur-md rounded-xl px-3 py-2 animate-[fadeIn_.2s_ease]">
              {note}
            </div>
          )}

          {blockErr === "creds" && (
            <CenterPanel>
              <div className="mx-auto h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                <Ic d={IC.phone} size={20} />
              </div>
              <div className="text-[15px] font-semibold text-white">MyHome credentials needed</div>
              <p className="text-[12px] text-white/50">
                Publishing needs your MyHome account (email, password and phone) saved in your profile.
              </p>
              <Link to="/profile" className="btn-primary inline-block !px-4 !py-2 !text-xs">
                Add credentials
              </Link>
              <button
                type="button"
                onClick={() => setBlockErr(null)}
                className="block mx-auto text-[11px] text-white/40 hover:text-white/70"
              >
                Keep swiping (reject only)
              </button>
            </CenterPanel>
          )}

          {blockErr === "scraper" && (
            <CenterPanel>
              <div className="text-[13px] text-rose-300">
                The listings scraper isn't reachable right now — try again in a minute.
              </div>
              <button type="button" onClick={onExit} className="btn-primary !px-4 !py-2 !text-xs">
                Back to the list
              </button>
            </CenterPanel>
          )}

          {loading && !blockErr && (
            <div className="absolute inset-0 animate-pulse bg-gradient-to-b from-white/[0.06] via-white/[0.02] to-white/[0.06]" />
          )}

          {!loading && !blockErr && top && (
            <>
              {/* Next card's cover pre-rendered underneath for instant reveal */}
              <NextUnder listing={next} />
              <StoryCard
                key={keyOf(top)}
                listing={top}
                disabled={busySwipe}
                sheetOpen={sheetOpen}
                onSheet={setSheetOpen}
                onReject={() => doReject(top)}
                onPublish={() => void doPublish(top)}
                onReport={() => setReportFor(top)}
                onExit={onExit}
                poolLabel={poolLabel}
                windowLabel={windowLabel}
                windowExtended={meta.extended && !meta.exhausted}
              />
            </>
          )}

          {reportFor && (
            <LabelReportDialog
              inline
              source={reportFor.source}
              externalId={reportFor.application_id}
              current={reportFor}
              onClose={() => setReportFor(null)}
              onSaved={(fields) => {
                setQueue((q) => q.map((x) => (keyOf(x) === keyOf(reportFor) ? { ...x, ...fields } : x)));
                setReportFor(null);
              }}
            />
          )}

          {!loading && !blockErr && !top && (
            <CenterPanel>
              {meta.exhausted ? (
                <>
                  <div
                    className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 h-64 w-64 rounded-full"
                    style={{ background: "radial-gradient(closest-side, rgba(16,185,129,0.25), transparent)" }}
                  />
                  <div className="relative text-6xl animate-[deckPop_.5s_cubic-bezier(.2,.9,.3,1.2)]">🎉</div>
                  <div className="relative text-[18px] font-semibold text-white">
                    You're all caught up — great job!
                  </div>
                  <p className="relative text-[12.5px] text-white/50">
                    Every fresh owner listing from the last week has been reviewed.
                  </p>
                  {stats.reviewed > 0 && (
                    <div className="relative flex items-center justify-center gap-2">
                      <span className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.06] px-2.5 py-1 text-[10.5px] font-semibold text-white/80 tabular-nums">
                        {stats.reviewed} reviewed
                      </span>
                      {stats.published > 0 && (
                        <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10.5px] font-semibold text-emerald-300 tabular-nums">
                          {stats.published} sent to publish
                        </span>
                      )}
                    </div>
                  )}
                  <button type="button" onClick={onExit} className="relative btn-primary !px-5 !py-2 !text-xs">
                    Back to the list
                  </button>
                </>
              ) : (
                <div className="text-[12.5px] text-white/50">Loading more…</div>
              )}
            </CenterPanel>
          )}
        </div>
      </div>

      {/* Desktop keyboard hints on the backdrop */}
      <div className="hidden sm:flex absolute bottom-4 inset-x-0 items-center justify-center gap-2.5 text-[10.5px] text-white/35 pointer-events-none">
        <Kbd>←</Kbd> skip
        <span className="text-white/20">·</span>
        <Kbd>→</Kbd> publish
        <span className="text-white/20">·</span>
        <Kbd>↑</Kbd><Kbd>↓</Kbd> photos
        <span className="text-white/20">·</span>
        <Kbd>↵</Kbd> details
        <span className="text-white/20">·</span>
        <Kbd>esc</Kbd> exit
      </div>
    </div>,
    document.body,
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-white/15 bg-white/[0.06] px-1.5 py-0.5 font-sans text-white/60">
      {children}
    </kbd>
  );
}

function CenterPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-6">
      <div className="relative w-full max-w-xs text-center space-y-3 animate-[deckIn_.3s_ease]">
        {children}
      </div>
    </div>
  );
}

function NextUnder({ listing }: { listing: DeckListing | null }) {
  if (!listing) return null;
  const cover = photosOf(listing)[0];
  return (
    <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden>
      {cover ? (
        <img src={cover} alt="" className="h-full w-full object-cover" loading="lazy" draggable={false} />
      ) : (
        <div className="h-full w-full bg-white/[0.03]" />
      )}
      <div className="absolute inset-0 bg-black/60" />
    </div>
  );
}

/* ── The story card: fullscreen photo + overlays + drag/tap + sheet ──────── */

function StoryCard({
  listing,
  disabled,
  sheetOpen,
  onSheet,
  onReject,
  onPublish,
  onReport,
  onExit,
  poolLabel,
  windowLabel,
  windowExtended,
}: {
  listing: DeckListing;
  disabled: boolean;
  sheetOpen: boolean;
  onSheet: (v: boolean) => void;
  onReject: () => void;
  onPublish: () => void;
  onReport: () => void;
  onExit: () => void;
  poolLabel: string;
  windowLabel: string;
  windowExtended: boolean;
}) {
  const photos = photosOf(listing);
  const n = photos.length;
  const [idx, setIdx] = useState(0);
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null);
  const [leaving, setLeaving] = useState<"left" | "right" | null>(null);
  const start = useRef<{ x: number; y: number; t: number } | null>(null);
  const hot = isHot(listing.create_date);
  const description = cleanDescription(listing.description);

  const step = useCallback(
    (d: number) => { if (n > 1) setIdx((i) => (i + d + n) % n); },
    [n],
  );

  // Preload the next photo of THIS card for seamless story switching.
  useEffect(() => {
    if (n > 1) { const im = new Image(); im.src = photos[(idx + 1) % n]; }
  }, [idx, n, photos]);

  // Keyboard: ↑/↓ photo nav, Enter opens the sheet (ESC handled deck-level).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (sheetOpen) return;
      if (e.key === "ArrowUp") { e.preventDefault(); step(-1); }
      if (e.key === "ArrowDown") { e.preventDefault(); step(1); }
      if (e.key === "Enter") { e.preventDefault(); onSheet(true); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheetOpen, step, onSheet]);

  function commit(dir: "left" | "right") {
    setLeaving(dir);
    setTimeout(() => (dir === "left" ? onReject() : onPublish()), 240);
  }

  function onPointerDown(e: React.PointerEvent) {
    if (disabled || leaving || sheetOpen) return;
    // Don't hijack presses on real controls (chips, links, orbs).
    if ((e.target as HTMLElement).closest("a,button")) return;
    start.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!start.current) return;
    setDrag({ dx: e.clientX - start.current.x, dy: e.clientY - start.current.y });
  }
  function onPointerUp(e: React.PointerEvent) {
    if (!start.current) return;
    const s = start.current;
    start.current = null;
    const dx = drag?.dx ?? 0;
    const dy = drag?.dy ?? 0;
    const dist = Math.hypot(dx, dy);
    const dt = Date.now() - s.t;
    setDrag(null);

    if (dist < TAP_MAX_DIST && dt < TAP_MAX_MS) {
      // IG tap zones: left third = previous photo, the rest = next.
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      if (e.clientX - rect.left < rect.width / 3) step(-1);
      else step(1);
      return;
    }
    if (Math.abs(dx) >= SWIPE_THRESHOLD) commit(dx < 0 ? "left" : "right");
  }

  const dx = leaving ? (leaving === "left" ? -720 : 720) : (drag?.dx ?? 0);
  const dy = leaving ? -60 : (drag?.dy ?? 0) * 0.15;
  const rot = Math.max(-14, Math.min(14, dx * 0.04));
  const stampOpacity = Math.min(1, Math.abs(dx) / SWIPE_THRESHOLD);
  const dragging = drag != null;

  return (
    <>
    <div
      className="absolute inset-0 z-10 select-none touch-none cursor-grab active:cursor-grabbing group animate-[deckIn_.3s_cubic-bezier(.2,.9,.3,1.12)]"
      style={{
        transform: `translate(${dx}px, ${dy}px) rotate(${rot}deg)`,
        transition: dragging
          ? "none"
          : leaving
            ? "transform 320ms cubic-bezier(.5,.05,.9,.4), opacity 300ms ease"
            : "transform 380ms cubic-bezier(.18,.89,.32,1.25)",
        opacity: leaving ? 0 : 1,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* Fullscreen photo */}
      <div className="absolute inset-0 bg-[#0c0c10]">
        {n > 0 ? (
          <img
            key={idx}
            src={photos[idx]}
            alt=""
            className="h-full w-full object-cover animate-[fadeIn_.25s_ease]"
            draggable={false}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-white/25 text-[12px]">
            no photos
          </div>
        )}
      </div>

      {/* Top scrim */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/80 via-black/40 to-transparent pointer-events-none" />

      {/* Segments + header chips (safe-area aware) */}
      <div className="absolute inset-x-0 top-0 z-20 safe-pt">
        {n > 1 && (
          <div className="flex gap-1 px-3 pt-2.5">
            {photos.map((_, i) => (
              <span
                key={i}
                className={
                  "h-[3px] flex-1 rounded-full transition-colors duration-200 " +
                  (i === idx ? "bg-white/95" : "bg-white/30")
                }
              />
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5 px-3 pt-2.5">
          <button
            type="button"
            onClick={onExit}
            aria-label="Exit deck"
            className="h-8 w-8 rounded-full bg-black/45 border border-white/15 text-white/90 backdrop-blur-md hover:bg-black/70 active:scale-95 transition-all flex items-center justify-center"
          >
            <Ic d={IC.x} size={14} sw={2.2} />
          </button>
          <span
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-black/45 border border-white/15 px-2 py-1 text-[10px] font-semibold text-white/85 backdrop-blur-md tabular-nums"
            title="Listings left to review"
          >
            <Ic d={IC.flame} size={10} className="text-accent-400" />
            {poolLabel}
          </span>
          <span
            className={
              "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-medium backdrop-blur-md " +
              (windowExtended
                ? "bg-amber-950/60 border border-amber-400/30 text-amber-300"
                : "bg-black/45 border border-white/15 text-white/70")
            }
            title={windowExtended ? "Pool widened — you cleared the newest listings" : "Reviewing the newest window"}
          >
            <Ic d={IC.clock} size={10} />
            {windowLabel}
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5">
            <span
              className={
                "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-semibold backdrop-blur-md " +
                (listing.source === "mh"
                  ? "bg-emerald-950/60 text-emerald-300 border border-emerald-400/30"
                  : "bg-sky-950/60 text-sky-300 border border-sky-400/30")
              }
            >
              <span className={"h-1.5 w-1.5 rounded-full " + (listing.source === "mh" ? "bg-emerald-400" : "bg-sky-400")} />
              {SOURCE_LABEL[listing.source]}
            </span>
            {hot && (
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-950/60 border border-orange-400/40 px-2 py-1 text-[10px] font-bold text-orange-300 backdrop-blur-md">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-orange-400" />
                </span>
                NEW
              </span>
            )}
            <span className="whitespace-nowrap rounded-full bg-black/45 border border-white/15 px-2 py-1 text-[10px] font-medium text-white/80 backdrop-blur-md tabular-nums">
              {timeAgo(listing.create_date)}
            </span>
          </span>
        </div>
      </div>

      {/* Desktop hover arrows */}
      {n > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); step(-1); }}
            className="hidden sm:flex absolute left-2.5 top-1/2 -translate-y-1/2 z-20 h-9 w-9 rounded-full bg-black/45 text-white/90 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:bg-black/70 hover:scale-110 active:scale-95 transition-all duration-150 items-center justify-center"
            aria-label="Previous photo"
          >
            <Ic d={IC.chevL} size={16} sw={2.5} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); step(1); }}
            className="hidden sm:flex absolute right-2.5 top-1/2 -translate-y-1/2 z-20 h-9 w-9 rounded-full bg-black/45 text-white/90 backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:bg-black/70 hover:scale-110 active:scale-95 transition-all duration-150 items-center justify-center"
            aria-label="Next photo"
          >
            <Ic d={IC.chevR} size={16} sw={2.5} />
          </button>
        </>
      )}

      {/* Bottom scrim with all the info */}
      <div className="absolute inset-x-0 bottom-0 z-20 pt-24 bg-gradient-to-t from-black/95 via-black/65 to-transparent">
        <div className="px-4 pb-2 space-y-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[27px] font-bold tabular-nums text-white leading-none drop-shadow">
              {fmtPrice(listing.price_usd)}
            </span>
            {listing.unit_price_usd != null && (
              <span className="text-[11.5px] text-white/65 tabular-nums font-medium">
                ${Math.round(listing.unit_price_usd).toLocaleString()}/m²
              </span>
            )}
            <span className="ml-auto text-[11.5px] text-white/75 font-medium">
              {[listing.deal_type, listing.real_estate_type].filter(Boolean).join(" · ")}
            </span>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <AttrPill icon={IC.area} value={listing.area ? `${listing.area} m²` : "—"} />
            <AttrPill icon={IC.rooms} value={listing.rooms != null ? `${listing.rooms} room${listing.rooms === 1 ? "" : "s"}` : "— rooms"} />
            <AttrPill
              icon={IC.floor}
              value={
                listing.floor != null
                  ? `fl ${listing.floor}${listing.floors_total ? "/" + listing.floors_total : ""}`
                  : "fl —"
              }
            />
          </div>

          <div className="flex items-center gap-1.5 text-[11.5px] text-white/70 min-w-0">
            <Ic d={IC.pin} size={13} className="shrink-0 text-white/45" />
            <span className="truncate" title={locationLine(listing)}>{locationLine(listing)}</span>
          </div>

          {description && (
            <div className="text-[11.5px] leading-snug text-white/60">
              <span className="line-clamp-2 whitespace-pre-line">{description}</span>
              <button
                type="button"
                onClick={() => onSheet(true)}
                className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-white/90 hover:text-white"
              >
                more
                <Ic d={IC.chevD} size={11} className="-rotate-90" />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <DeckPhones listing={listing} />
            <a
              href={listing.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-accent-300 hover:text-accent-200 whitespace-nowrap transition-colors"
            >
              <Ic d={IC.open} size={12} />
              Open
            </a>
          </div>
        </div>

        {/* Action orbs — an identical matched pair, only the hue differs */}
        <div className="flex items-center justify-center gap-9 pt-1.5 pb-4 safe-pb">
          <ActionOrb
            title="Reject (←)"
            disabled={disabled}
            onClick={() => !disabled && commit("left")}
            gradient="linear-gradient(135deg, #fb7185, #e11d48)"
            glow="rgba(225,29,72,0.55)"
          >
            <Ic d={IC.x} size={22} sw={2.4} />
          </ActionOrb>
          <ActionOrb
            title="Publish to both portals (→)"
            disabled={disabled}
            onClick={() => !disabled && commit("right")}
            gradient="linear-gradient(135deg, #34d399, #059669)"
            glow="rgba(16,185,129,0.55)"
          >
            <Ic d={IC.send} size={22} sw={2.2} className="-translate-x-0.5 translate-y-0.5" />
          </ActionOrb>
        </div>
      </div>

      {/* Details bottom sheet */}
      {sheetOpen && <DetailSheet listing={listing} onClose={() => onSheet(false)} onReport={onReport} />}
    </div>

      {/* Static swipe overlay — NOT transformed with the card, so the SKIP /
          PUBLISH signal holds its place and stays readable while the card flies
          out (dx peaks at ±720 on commit → full opacity). pointer-events-none so
          it never blocks the card or the action orbs beneath it. */}
      <div className="absolute inset-0 z-40 pointer-events-none">
        {/* Full-frame directional tint */}
        <div
          className="absolute inset-0"
          style={{
            opacity: (dx < 0 ? stampOpacity : 0) * 0.55,
            background:
              "radial-gradient(120% 90% at 0% 50%, rgba(225,29,72,0.5), transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            opacity: (dx > 0 ? stampOpacity : 0) * 0.55,
            background:
              "radial-gradient(120% 90% at 100% 50%, rgba(16,185,129,0.5), transparent 60%)",
          }}
        />
        <span
          className="absolute top-24 left-5 rounded-xl border-[3px] border-rose-400/90 bg-rose-950/40 backdrop-blur-sm px-3 py-1 text-[18px] font-extrabold tracking-widest text-rose-300 shadow-lg shadow-rose-500/20"
          style={{ opacity: dx < 0 ? stampOpacity : 0, transform: `rotate(-14deg) scale(${0.75 + 0.3 * (dx < 0 ? stampOpacity : 0)})` }}
        >
          SKIP
        </span>
        <span
          className="absolute top-24 right-5 rounded-xl border-[3px] border-emerald-400/90 bg-emerald-950/40 backdrop-blur-sm px-3 py-1 text-[18px] font-extrabold tracking-widest text-emerald-300 shadow-lg shadow-emerald-500/20"
          style={{ opacity: dx > 0 ? stampOpacity : 0, transform: `rotate(14deg) scale(${0.75 + 0.3 * (dx > 0 ? stampOpacity : 0)})` }}
        >
          PUBLISH
        </span>
      </div>
    </>
  );
}

/* One shared orb so reject/publish can never drift apart in size or style. */
function ActionOrb({
  title,
  disabled,
  onClick,
  gradient,
  glow,
  children,
}: {
  title: string;
  disabled: boolean;
  onClick: () => void;
  gradient: string;
  glow: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="rounded-full text-white hover:scale-105 active:scale-90 transition-all duration-150 flex items-center justify-center disabled:opacity-40"
      style={{
        height: "3.5rem",
        width: "3.5rem",
        background: gradient,
        boxShadow: `0 8px 28px -6px ${glow}, inset 0 1px 0 rgba(255,255,255,0.28)`,
      }}
    >
      {children}
    </button>
  );
}

function AttrPill({ icon, value }: { icon: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.09] border border-white/10 backdrop-blur-sm px-2.5 py-1 text-[11px] font-semibold tabular-nums text-white/90">
      <Ic d={icon} size={12} className="text-white/55" />
      {value}
    </span>
  );
}

function DeckPhones({ listing }: { listing: DeckListing }) {
  const p = listing.phones[0];
  if (!p) return <span className="text-[11px] text-white/35">no phone in data</span>;
  const masked = p.number.includes("*");
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      {masked ? (
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.07] backdrop-blur-sm px-2.5 py-1 text-[12px] font-mono tabular-nums text-white/75">
          <Ic d={IC.phone} size={11} className="text-white/45" />
          {p.number}
        </span>
      ) : (
        <a
          href={`tel:+995${p.number}`}
          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-950/50 backdrop-blur-sm px-2.5 py-1 text-[12px] font-mono tabular-nums text-emerald-300 hover:bg-emerald-500/25 transition-colors"
        >
          <Ic d={IC.phone} size={11} />
          {p.number}
        </a>
      )}
      <span className="inline-flex items-center gap-1 text-[11px] text-white/55 min-w-0">
        <Ic d={IC.user} size={11} className="shrink-0 text-white/35" />
        {listing.contact_person && (
          <span className="truncate max-w-[5.5rem]" title={listing.contact_person}>
            {listing.contact_person}
          </span>
        )}
        {/* The feed is server-filtered to owners, with undeclared agencies
            excluded — but render from the data rather than hardcoding the
            word. The label used to be a static "owner", which would quietly
            start lying the moment that predicate changed. */}
        {listing.undeclared_agency ? (
          <span
            className="text-[9.5px] uppercase tracking-wider font-semibold text-orange-400/90"
            title="Listed as an owner, but this looks like an agency"
          >
            {listing.agency_name ?? "agency?"}
          </span>
        ) : (
          <span className="text-[9.5px] uppercase tracking-wider font-semibold text-emerald-400/90">
            {listing.seller_type === "owner" ? "owner" : listing.seller_type ?? "—"}
          </span>
        )}
        {listing.agent_label && (
          <>
            <span className="text-white/25">·</span>
            <AgentLabelBadge fields={listing} variant="inline" />
          </>
        )}
      </span>
    </span>
  );
}

/* ── Bottom sheet: full description + every detail, scrolls INSIDE ───────── */

function DetailSheet({
  listing,
  onClose,
  onReport,
}: {
  listing: DeckListing;
  onClose: () => void;
  onReport: () => void;
}) {
  const startY = useRef<number | null>(null);
  const description = cleanDescription(listing.description);

  return (
    <div className="absolute inset-0 z-40 flex flex-col justify-end">
      {/* Dim layer over the photo — tap to close */}
      <button
        type="button"
        aria-label="Close details"
        onClick={onClose}
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px] animate-[fadeIn_.2s_ease] cursor-default"
      />
      <div
        className="relative max-h-[72%] flex flex-col rounded-t-2xl border-t border-white/15 bg-[#101014]/95 backdrop-blur-xl shadow-2xl animate-[deckSheetUp_.28s_cubic-bezier(.2,.9,.3,1.05)]"
        onPointerDown={(e) => { startY.current = e.clientY; }}
        onPointerUp={(e) => {
          // Swipe-down on the sheet header closes it.
          if (startY.current != null && e.clientY - startY.current > 70) onClose();
          startY.current = null;
        }}
      >
        <div className="shrink-0 pt-2.5 pb-1 flex flex-col items-center gap-2 cursor-grab">
          <span className="h-1 w-10 rounded-full bg-white/25" />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2.5 right-3 h-7 w-7 rounded-full bg-white/[0.07] border border-white/10 text-white/70 hover:text-white hover:bg-white/[0.14] transition-colors flex items-center justify-center"
        >
          <Ic d={IC.x} size={12} sw={2.2} />
        </button>

        <div className="overflow-y-auto scrollbar-thin px-4 pb-5 safe-pb space-y-3.5 touch-pan-y">
          <div className="pr-8">
            <div className="text-[14.5px] font-semibold text-white leading-snug">
              {listing.title || "Listing details"}
            </div>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <span className="text-[19px] font-bold tabular-nums text-white">{fmtPrice(listing.price_usd)}</span>
              {listing.price_geo != null && (
                <span className="text-[11px] text-white/50 tabular-nums">₾{Math.round(listing.price_geo).toLocaleString()}</span>
              )}
              {listing.unit_price_usd != null && (
                <span className="text-[11px] text-white/50 tabular-nums">
                  ${Math.round(listing.unit_price_usd).toLocaleString()}/m²
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-3">
            <SheetRow icon={IC.tag} label="Deal" value={[listing.deal_type, listing.real_estate_type].filter(Boolean).join(" · ") || "—"} />
            <SheetRow icon={IC.area} label="Area" value={listing.area ? `${listing.area} m²` : "—"} />
            <SheetRow icon={IC.rooms} label="Rooms" value={listing.rooms != null ? String(listing.rooms) : "—"} />
            <SheetRow
              icon={IC.floor}
              label="Floor"
              value={listing.floor != null ? `${listing.floor}${listing.floors_total ? " / " + listing.floors_total : ""}` : "—"}
            />
            <SheetRow icon={IC.clock} label="Posted" value={fmtDate(listing.create_date)} />
            <SheetRow icon={IC.pin} label="Location" value={locationLine(listing)} />
          </div>

          {description && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-1">Description</div>
              <p className="text-[12.5px] leading-relaxed text-white/80 whitespace-pre-wrap">
                {description}
              </p>
            </div>
          )}

          {/* Agent-posture label + feedback. Always shown for owner rows so a
              missed phrase can be taught even when there is no label yet. */}
          <div className="rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-1">Agents</div>
                {listing.agent_label ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <AgentLabelBadge fields={listing} />
                    {listing.agent_label_reason && (
                      <span className="text-[11px] text-white/55 truncate" title={agentLabelTitle(listing)}>
                        {listing.agent_label_reason}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-[11.5px] text-white/45">Nothing about agents detected.</div>
                )}
                {listing.agent_label_evidence && (
                  <div className="mt-1 text-[11px] text-white/60 italic truncate" title={listing.agent_label_evidence}>
                    “{listing.agent_label_evidence}”
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onReport}
                className="shrink-0 rounded-lg border border-white/15 bg-white/[0.06] px-2.5 py-1.5 text-[11px] font-medium text-white/75 hover:text-white hover:bg-white/[0.12] transition-colors"
              >
                {listing.agent_label ? "Wrong label?" : "Label it"}
              </button>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-1.5">Contact</div>
            <div className="flex items-center gap-2 flex-wrap">
              {listing.phones.length === 0 && <span className="text-[11px] text-white/35">no phone in data</span>}
              {listing.phones.map((p) =>
                p.number.includes("*") ? (
                  <span key={p.number} className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.06] px-2.5 py-1.5 text-[12.5px] font-mono tabular-nums text-white/75">
                    <Ic d={IC.phone} size={12} className="text-white/45" />
                    {p.number}
                  </span>
                ) : (
                  <a
                    key={p.number}
                    href={`tel:+995${p.number}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 text-[12.5px] font-mono tabular-nums text-emerald-300 hover:bg-emerald-500/20 transition-colors"
                  >
                    <Ic d={IC.phone} size={12} />
                    {p.number}
                  </a>
                ),
              )}
              {listing.contact_person && (
                <span className="inline-flex items-center gap-1.5 text-[12px] text-white/60">
                  <Ic d={IC.user} size={12} className="text-white/40" />
                  {listing.contact_person}
                </span>
              )}
            </div>
          </div>

          <a
            href={listing.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-accent-500/35 bg-accent-500/10 px-3 py-1.5 text-[12px] font-medium text-accent-300 hover:bg-accent-500/20 transition-colors"
          >
            <Ic d={IC.open} size={12} />
            Open on {SOURCE_LABEL[listing.source]}
          </a>
        </div>
      </div>
    </div>
  );
}

function SheetRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Ic d={icon} size={13} className="shrink-0 text-white/40" />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-wider text-white/40 font-semibold leading-tight">{label}</div>
        <div className="text-[12px] font-medium text-white/85 leading-tight truncate" title={value}>{value}</div>
      </div>
    </div>
  );
}
